import { useCallback, useMemo, useState } from 'react';

/**
 * Row selection of the Members table. Keeps the selected member OBJECTS (by id) so the bulk rules
 * can look at their status and user type, and the selection survives paging. Selected members that
 * are on screen are refreshed from the latest rows. Shaped for `DataTable`'s `selection` prop.
 *
 * @param {{ id: string }[]} rows Members of the current page.
 */
export const useMemberSelection = (rows) => {
  const [selected, setSelected] = useState(() => new Map());

  const rowsById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  const isRowSelected = useCallback((row) => selected.has(row.id), [selected]);

  const onToggleRow = useCallback((row) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  }, []);

  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

  const onToggleAll = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      const everyOnPage = rows.length > 0 && rows.every((row) => next.has(row.id));
      rows.forEach((row) => (everyOnPage ? next.delete(row.id) : next.set(row.id, row)));
      return next;
    });
  }, [rows]);

  // Adds members that may not be on screen (e.g. "Select the N not invited" of an import).
  const select = useCallback((members) => {
    setSelected((prev) => {
      const next = new Map(prev);
      members.forEach((member) => next.set(member.id, member));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Map()), []);

  const deselect = useCallback((ids) => {
    setSelected((prev) => {
      const next = new Map(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  return useMemo(
    () => ({
      count: selected.size,
      selectedMembers: [...selected.values()].map((m) => rowsById.get(m.id) ?? m),
      allSelected,
      isRowSelected,
      onToggleRow,
      onToggleAll,
      select,
      clear,
      deselect,
    }),
    [selected, rowsById, allSelected, isRowSelected, onToggleRow, onToggleAll, select, clear, deselect],
  );
};
